import { fetchJson, postJson } from '@comeon/utility';
import { updateFrame } from './use-analytics.js';
import { reportException } from '@comeon/error-reporter';

const delayInMilliseconds = 1000;

const handleTrackingResponse = (response, requestUrl, cb) => {
	if (response && response?.result?.eventInfo) {
		const { eventInfo } = response.result;
		window.dataLayer = window.dataLayer || [];
		// TODO Remove after GA is updated with new setup (remove pixel_ prefix)
		const event = eventInfo.replace(
			"'pixel_device': 'Mobile'",
			`'pixel_device': ${/Mobi|Android/i.test(navigator.userAgent) ? "'Mobile'" : "'Desktop'"}`
		);

		const jsonEvent = JSON.parse(event.replace(/'/g, '"'));
		window.dataLayer.push(jsonEvent);
	} else if (response.status === 'FAILURE') {
		reportException(
			new Error(`pixel-tracking error for ${requestUrl}, ${response.errorCode}`, {
				cause: response.errorDescription
			}),
			{ domain: 'analytics' }
		);
	}
	if (typeof cb === 'function') {
		cb(response.result.eventInfo);
	}
};

const fetchTrackingWithoutSession = (requestUrl, data) => {
	postJson(requestUrl, data)
		.then(response => handleTrackingResponse(response, requestUrl))
		.catch(e => {
			reportException(new Error('pixel-tracking failed requesting data', { cause: e }), { domain: 'analytics' });
		});
};

const fetchTracking = (requestUrl, data, cb) => {
	fetchJson(requestUrl, data, { priority: 'low' })
		.then(response => handleTrackingResponse(response, requestUrl, cb))
		.catch(e => {
			reportException(new Error('pixel-tracking failed requesting data', { cause: e }), { domain: 'analytics' });
		});
};

const fetchOldPixel = (name, data, cb) => {
	const requestUrl = `/tracking/?old_pixel=${name}`;
	fetchTracking(requestUrl, data, cb);
};

const fetchFrame = (name, data, cb) => {
	const requestUrl = `/tracking/?frame=${name}`;
	fetchTracking(requestUrl, data, cb);
};

const insertInitialMetaForFrame = (name, dispatch, callBackFunction) => {
	if (!name) {
		// eslint-disable-next-line no-console
		console.error('Pixel requested without name');
		return;
	}
	fetchFrame(name, undefined, (eventInfo, aName) => {
		if (typeof callBackFunction === 'function') {
			callBackFunction(eventInfo, aName);
		}
		updateFrame(aName, dispatch);
	});
};

const requestInitialMetaFromStore = ({ frame, cb, delay }) => {
	const data = {};
	// avoid to re-trigger events when app is remounted or triggering after bankId login from landing page
	if (frame === 'site' || frame === 'start') {
		return;
	}
	data.frame = frame || 'site';
	if (delay) {
		setTimeout(() => {
			fetchFrame(data.frame, undefined, cb);
		}, delayInMilliseconds);
		return;
	}
	fetchFrame(data.frame, data, cb);
};

const insertOldPixel = async (name, cb) => {
	setTimeout(() => {
		fetchOldPixel(name, undefined, cb);
	}, delayInMilliseconds);
};

const insertOldPixelWithoutSession = async encryptedPID => {
	const requestUrl = `/tracking`;
	fetchTrackingWithoutSession(requestUrl, encryptedPID);
};

const insertDepositPixel = (data, cb) => {
	const paramsObj = data || {};
	paramsObj.old_pixel = 'deposit';
	const requestUrl = '/tracking/';
	fetchTracking(requestUrl, paramsObj, cb);
};

const insertDataLayer = (newEvent, params = {}, clonableProps = []) => {
	window.dataLayer = window.dataLayer || [];
	const newDataLayer = {
		...window.dataLayer.findLast(e => {
			return e.event === 'initial-meta' && e.frame === 'site';
		})
	};
	if (newDataLayer) {
		// TODO Remove after GA is updated with new setup (remove pixel_ prefix)
		const ndl = {
			event: newEvent,
			pixel_country: newDataLayer.pixel_country,
			pixel_language: newDataLayer.pixel_language,
			pixel_affiliateId: newDataLayer.pixel_affiliateId,
			pixel_site: newDataLayer.pixel_site,
			pixel_device: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
			pixel_time: new Date().toISOString(),
			pixel_playerId: newDataLayer?.pixel_playerId,
			pixel_depositCounts: newDataLayer?.pixel_depositCounts,
			pixel_currentValue: newDataLayer?.pixel_currentValue,
			pixel_lastDeposit: newDataLayer?.pixel_lastDeposit,
			pixel_leadProduct: newDataLayer?.pixel_leadProduct,
			pixel_currentProduct: newDataLayer?.pixel_currentProduct,
			pixel_highestValue: newDataLayer?.pixel_highestValue,
			pixel_franchiseName: newDataLayer?.pixel_franchiseName,
			pixel_login: newDataLayer?.pixel_login,
			pixel_lastDepositDate: newDataLayer?.pixel_lastDepositDate,
			pixel_internal: newDataLayer?.pixel_internal,
			pixel_timestamp: newDataLayer?.pixel_timestamp,
			pixel_cv: newDataLayer?.pixel_cv,
			pixel_cwl: newDataLayer?.pixel_cwl,
			pixel_cwr: newDataLayer?.pixel_cwr,
			pixel_cw: newDataLayer?.pixel_cw,
			pixel_player_email: newDataLayer?.pixel_player_email,
			pixel_player_phone: newDataLayer?.pixel_player_phone,
			pixel_player_address: newDataLayer?.pixel_player_address,
			pixel_player_postcode: newDataLayer?.pixel_player_postcode,
			pixel_player_first_name: newDataLayer?.pixel_player_first_name,
			pixel_player_last_name: newDataLayer?.pixel_player_last_name,
			pixel_player_birthdate: newDataLayer?.pixel_player_birthdate,
			pixel_player_gender: newDataLayer?.pixel_player_gender,
			pixel_player_city: newDataLayer?.pixel_player_city,
			...params
		};
		// FIXME
		// eslint-disable-next-line no-restricted-syntax
		for (const clonableProp of clonableProps) {
			ndl[clonableProp] = newDataLayer[clonableProp];
		}
		window.dataLayer.push(ndl);
	} else {
		// eslint-disable-next-line no-console
		console.error(`Could not add event to dataLayer: ${newEvent}`);
	}
};

const insertTrackEvent = ({ category, action, label, value = '', ...rest }) => {
	window.dataLayer = window.dataLayer || [];

	window.dataLayer.push({
		event: 'trackEvent',
		eventCategory: category,
		eventAction: action,
		eventLabel: label,
		eventValue: value,
		...rest
	});
};

const eCommerceTrackEvent = ({ event, eComObj }) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event,
		ecommerce: eComObj
	});
};

export {
	eCommerceTrackEvent,
	insertDataLayer,
	insertDepositPixel,
	insertInitialMetaForFrame,
	insertOldPixel,
	insertOldPixelWithoutSession,
	insertTrackEvent,
	requestInitialMetaFromStore
};
export default {};
